import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTACTS, VENUE_NAME, VENUE_ADDRESS } from '../constants/wedding.ts';
import { ColorScheme } from '../constants/colors.ts';

interface QuickActionsProps {
  isPlaying: boolean;
  toggleMusic: () => void;
  scrollToRSVP: () => void;
  colors: ColorScheme;
}

const QuickActions: React.FC<QuickActionsProps> = ({ 
  isPlaying, 
  toggleMusic, 
  scrollToRSVP,
  colors
}) => {
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [showContacts, setShowContacts] = useState(false);

  const openGoogleMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(VENUE_NAME)}`);
    setShowMapOptions(false);
  };

  const openWaze = () => {
    window.open(`https://waze.com/ul?q=${encodeURIComponent(VENUE_NAME)}&navigate=yes`);
    setShowMapOptions(false);
  };

  const handleMapOptionClick = (action: () => void) => {
    action();
    setShowMapOptions(false);
  };

  const handleCall = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
    setShowContacts(false);
  };

  const closeAllSliders = () => {
    setShowMapOptions(false);
    setShowContacts(false);
  };

  const toggleContacts = () => {
    if (showContacts) {
      setShowContacts(false);
    } else {
      closeAllSliders();
      setShowContacts(true);
    }
  };

  const toggleMapOptions = () => {
    if (showMapOptions) {
      setShowMapOptions(false);
    } else {
      closeAllSliders();
      setShowMapOptions(true);
    }
  };

  // Consistent button styles
  const buttonStyles = {
    primary: {
      base: "w-full font-medium py-4 rounded-lg border-2 transition-all duration-200 touch-manipulation",
      style: {
        backgroundColor: colors.primary,
        color: colors.text,
        borderColor: colors.primary,
        WebkitTapHighlightColor: 'transparent'
      },
      hoverStyle: {
        backgroundColor: colors.secondary,
        color: colors.primary,
        borderColor: colors.primary
      }
    },
    secondary: {
      base: "w-full font-medium py-4 rounded-lg border-2 transition-all duration-200 touch-manipulation",
      style: {
        backgroundColor: colors.secondary,
        color: colors.primary,
        borderColor: colors.primary,
        WebkitTapHighlightColor: 'transparent'
      },
      hoverStyle: {
        backgroundColor: colors.primary,
        color: colors.text,
        borderColor: colors.primary
      }
    },
    contact: {
      base: "w-full bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 touch-manipulation",
      style: {
        WebkitTapHighlightColor: 'transparent'
      },
      hoverStyle: {
        backgroundColor: '#f9fafb'
      }
    },
    icon: {
      base: "p-2 rounded-full transition-colors duration-200 touch-manipulation",
      style: {
        color: '#9ca3af',
        WebkitTapHighlightColor: 'transparent'
      },
      hoverStyle: {
        color: '#6b7280',
        backgroundColor: '#f3f4f6'
      }
    }
  };

  return (
    <>
      <AnimatePresence>
        {showContacts && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
            onClick={() => setShowContacts(false)}
          >
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "easeInOut", damping: 20, stiffness: 200 }}
              className="rounded-t-xl p-4 mb-20 w-full max-w-[430px] space-y-4 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
              style={{ 
                background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryHover})`,
                WebkitTapHighlightColor: 'transparent' 
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-serif" style={{ color: colors.text }}>Contact Us</h3>
                <button 
                  onClick={() => setShowContacts(false)}
                  className={buttonStyles.icon.base}
                  style={buttonStyles.icon.style}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.icon.hoverStyle);
                  }}
                  onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.icon.style);
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {CONTACTS.map((contact, index) => (
                  <button
                    key={index}
                    onClick={() => handleCall(contact.phone)}
                    className={buttonStyles.contact.base}
                    style={buttonStyles.contact.style}
                    onMouseEnter={(e) => {
                      Object.assign(e.currentTarget.style, buttonStyles.contact.hoverStyle);
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 text-left">
                        <p className="text-lg font-medium mb-0.5" style={{ color: colors.text }}>{contact.name}</p>
                        <p className="text-sm text-gray-500">{contact.role}</p>
                      </div>
                      <div className="text-white p-2.5 rounded-full ml-4 flex-shrink-0 shadow-sm" style={{ backgroundColor: colors.accent }}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}

        {showMapOptions && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
            onClick={() => setShowMapOptions(false)}
          >
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "easeInOut", damping: 20, stiffness: 200 }}
              className="rounded-t-xl p-4 mb-20 w-full max-w-[430px] space-y-4 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
              style={{ 
                background: `linear-gradient(to bottom, ${colors.primary}, ${colors.primaryHover})`,
                WebkitTapHighlightColor: 'transparent' 
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-serif" style={{ color: colors.text }}>Location</h3>
                <button 
                  onClick={() => setShowMapOptions(false)}
                  className={buttonStyles.icon.base}
                  style={buttonStyles.icon.style}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.icon.hoverStyle);
                  }}
                  onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.icon.style);
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Venue Information Block */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.accent }}>
                    <svg className="w-5 h-5" style={{ color: colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-lg mb-1" style={{ color: colors.text }}>{VENUE_NAME}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{VENUE_ADDRESS}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleMapOptionClick(openGoogleMaps)}
                  className={buttonStyles.secondary.base}
                  style={buttonStyles.secondary.style}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.secondary.hoverStyle);
                  }}
                  onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.secondary.style);
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 367"><path fill="#34A853" d="M70.585 271.865a370.712 370.712 0 0 1 28.911 42.642c7.374 13.982 10.448 23.463 15.837 40.31c3.305 9.308 6.292 12.086 12.714 12.086c6.998 0 10.173-4.726 12.626-12.035c5.094-15.91 9.091-28.052 15.397-39.525c12.374-22.15 27.75-41.833 42.858-60.75c4.09-5.354 30.534-36.545 42.439-61.156c0 0 14.632-27.035 14.632-64.792c0-35.318-14.43-59.813-14.43-59.813l-41.545 11.126l-25.23 66.451l-6.242 9.163l-1.248 1.66l-1.66 2.078l-2.914 3.319l-4.164 4.163l-22.467 18.304l-56.17 32.432l-9.344 54.337Z"/><path fill="#FBBC04" d="M12.612 188.892c13.709 31.313 40.145 58.839 58.031 82.995l95.001-112.534s-13.384 17.504-37.662 17.504c-27.043 0-48.89-21.595-48.89-48.825c0-18.673 11.234-31.501 11.234-31.501l-64.489 17.28l-13.225 75.08Z"/><path fill="#4285F4" d="M166.705 5.787c31.552 10.173 58.558 31.53 74.893 63.023l-75.925 90.478s11.234-13.06 11.234-31.617c0-27.864-23.463-48.68-48.81-48.68c-23.969 0-37.735 17.475-37.735 17.475v-57l76.343-33.68Z"/><path fill="#1A73E8" d="M30.015 45.765C48.86 23.218 82.02 0 127.736 0c22.18 0 38.89 5.823 38.89 5.823L90.29 96.516H36.205l-6.19-50.75Z"/><path fill="#EA4335" d="M12.612 188.892S0 164.194 0 128.414c0-33.817 13.146-63.377 30.015-82.649l60.318 50.759l-77.721 92.368Z"/></svg>
                    <span>Google Maps</span>
                  </div>
                </button>
                <button
                  onClick={() => handleMapOptionClick(openWaze)}
                  className={buttonStyles.secondary.base}
                  style={buttonStyles.secondary.style}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.secondary.hoverStyle);
                  }}
                  onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, buttonStyles.secondary.style);
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M13.218 0C9.915 0 6.835 1.49 4.723 4.148c-1.515 1.913-2.31 4.272-2.31 6.706v1.739c0 .894-.62 1.738-1.862 1.813c-.298.025-.547.224-.547.522c-.05.82.82 2.31 2.012 3.502c.82.844 1.788 1.515 2.832 2.036a3 3 0 0 0 2.955 3.528a2.966 2.966 0 0 0 2.931-2.385h2.509c.323 1.689 2.086 2.856 3.974 2.21c1.64-.546 2.36-2.409 1.763-3.924a12.84 12.84 0 0 0 1.838-1.465a10.73 10.73 0 0 0 3.18-7.65c0-2.882-1.118-5.589-3.155-7.625A10.899 10.899 0 0 0 13.218 0zm0 1.217c2.558 0 4.967.994 6.78 2.807a9.525 9.525 0 0 1 2.807 6.78A9.526 9.526 0 0 1 20 17.585a9.647 9.647 0 0 1-6.78 2.807h-2.46a3.008 3.008 0 0 0-2.93-2.41a3.03 3.03 0 0 0-2.534 1.367v.024a8.945 8.945 0 0 1-2.41-1.788c-.844-.844-1.316-1.614-1.515-2.11a2.858 2.858 0 0 0 1.441-.846a2.959 2.959 0 0 0 .795-2.036v-1.789c0-2.11.696-4.197 2.012-5.861c1.863-2.385 4.62-3.726 7.6-3.726zm-2.41 5.986a1.192 1.192 0 0 0-1.191 1.192a1.192 1.192 0 0 0 1.192 1.193A1.192 1.192 0 0 0 12 8.395a1.192 1.192 0 0 0-1.192-1.192zm7.204 0a1.192 1.192 0 0 0-1.192 1.192a1.192 1.192 0 0 0 1.192 1.193a1.192 1.192 0 0 0 1.192-1.193a1.192 1.192 0 0 0-1.192-1.192zm-7.377 4.769a.596.596 0 0 0-.546.845a4.813 4.813 0 0 0 4.346 2.757a4.77 4.77 0 0 0 4.347-2.757a.596.596 0 0 0-.547-.845h-.025a.561.561 0 0 0-.521.348a3.59 3.59 0 0 1-3.254 2.061a3.591 3.591 0 0 1-3.254-2.061a.64.64 0 0 0-.546-.348z"/></svg>
                    <span>Waze</span>
                  </div>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom navigation remains the same */}
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ 
          type: "tween",
          duration: 0.3,
          ease: "easeInOut",
        }}
        className="fixed bottom-0 left-0 right-0 py-4 px-6 z-[200] max-w-[430px] mx-auto backdrop-blur-sm"
        style={{ 
          backgroundColor: `${colors.primary}F2`,
          color: colors.text,
          boxShadow: `0 -4px 20px ${colors.shadow}`
        }}
      >
        <div className="flex justify-between items-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleContacts}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors.text }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="text-xs font-medium" style={{ color: colors.text, opacity: 0.9 }}>Contact</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleMapOptions}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors.text }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-xs font-medium" style={{ color: colors.text, opacity: 0.9 }}>Location</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToRSVP}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors.text }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs font-medium" style={{ color: colors.text, opacity: 0.9 }}>RSVP</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleMusic}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
            aria-label={isPlaying ? 'Pause Music' : 'Play Music'}
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors.text }}>
              {isPlaying ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span className="text-xs font-medium" style={{ color: colors.text, opacity: 0.9 }}>{isPlaying ? 'Pause' : 'Play'}</span>
          </motion.button>
        </div>
      </motion.div>
    </>
  );
};

export default QuickActions;
